import { useEffect, useRef } from "react";
import { useEnhancementPolling } from "@design-lab/shared/hooks/useEnhancementPolling";
import type { Artwork } from "../../artwork/types";

interface UseTransfersPollingOptions {
  artworkId?: number | undefined;
  startTime?: number | undefined;
  onEnhancementComplete?: (artwork: Artwork) => void;
  enabled?: boolean;
}

/**
 * Hook that provides fallback polling for transfers lab enhancement.
 * Starts polling 40 seconds after enhancement begins if websocket updates fail.
 */
export function useTransfersPolling({
  artworkId,
  startTime,
  onEnhancementComplete,
  enabled = true
}: UseTransfersPollingOptions) {
  const hasCompletedEnhancement = useRef(false);

  const { startPolling, stopPolling, isPolling } = useEnhancementPolling({
    artworkId: artworkId || 0,
    startTime: startTime || Date.now(),
    onEnhancementComplete: (artwork: Artwork) => {
      if (!hasCompletedEnhancement.current && onEnhancementComplete) {
        hasCompletedEnhancement.current = true;
        onEnhancementComplete(artwork);
      }
    },
    enabled: enabled && !!artworkId && !!startTime,
    isArtworkPresent: !!artworkId
  });

  // Start polling when conditions are met
  useEffect(() => {
    if (enabled && artworkId && startTime && !hasCompletedEnhancement.current) {
      startPolling();
    }
  }, [enabled, artworkId, startTime, startPolling]);

  // Reset completion state when artwork changes
  useEffect(() => {
    hasCompletedEnhancement.current = false;
  }, [artworkId]);

  return {
    isPolling,
    stopPolling
  };
}
